/*
 * Title: Reconcile In Bulk - ReconcileModal
 * Description: Modal for reviewing and executing reconciliation
 */

import { useEffect, useState } from 'react';
import { Accordion, Badge, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useReconcileInBulk } from './ContextAPI';

function ReconcileModal() {
    const {
        showModal,
        modalRowData,
        closeModal,
        handleExecute,
        executing,
        suppliers,
        manufacturers,
        fetchSuppliers,
        fetchManufacturers,
    } = useReconcileInBulk();

    const [adjustedQty, setAdjustedQty] = useState(null);
    const [executingLocal, setExecutingLocal] = useState(false);
    const [extraFields, setExtraFields] = useState({});

    useEffect(() => {
        if (modalRowData) {
            setAdjustedQty(modalRowData.expected_qty);
            const src = modalRowData.source || {};
            setExtraFields({
                supplier_id: src.supplier_id || '',
                manufacturer_id: src.manufacturer_id || '',
                currency_rate: src.currency_rate || '120.0000',
                currency_type: src.currency_type || 'usd',
                currency_amount: src.currency_amount || 0,
                unit_price: src.unit_price || 0,
                expire_date: src.expire_date || '',
                country_id: src.country_id || '',
                pack_no: src.pack_no || '1',
                pack_size: src.pack_size || '',
                comment: src.comment || '',
                mfg_date: src.mfg_date || '',
                po_drug_id: src.po_drug_id || '',
                center_id: src.warehouse_id || '',
            });
        }
    }, [modalRowData]);

    useEffect(() => {
        if (showModal) {
            if (!suppliers.length) fetchSuppliers();
            if (!manufacturers.length) fetchManufacturers();
        }
    }, [showModal, suppliers.length, manufacturers.length, fetchSuppliers, fetchManufacturers]);

    if (!showModal || !modalRowData) return null;

    const { code_no, drug_name, po_no, batch_no, available_qty, expected_qty, status, changes, reduction_details, gain_qty, loss_qty } = modalRowData;
    const type = status?.type || 'equal';
    const diff = status?.diff || 0;

    const batchDisplay = batch_no && batch_no.toUpperCase() === 'N/A' ? 'N/A' : batch_no;

    const handleExecuteClick = async () => {
        setExecutingLocal(true);
        const success = await handleExecute(modalRowData.detail_id, adjustedQty, extraFields);
        if (success) {
            setAdjustedQty(null);
            setExtraFields({});
        }
        setExecutingLocal(false);
    };

    const getTypeColor = () => {
        switch (type) {
            case 'gain': return 'success';
            case 'loss': return 'danger';
            default: return 'info';
        }
    };

    const getTypeLabel = () => {
        switch (type) {
            case 'gain': return `GAIN (+${diff})`;
            case 'loss': return `LOSS (${diff})`;
            default: return 'EQUAL';
        }
    };

    const updateField = (field, value) => {
        setExtraFields(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Modal show={showModal} onHide={closeModal} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <strong>{code_no}</strong> - {drug_name}
                    <Badge bg={getTypeColor()} className="ms-2 fs-6">{getTypeLabel()}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-3">
                    <Col md={3}>
                        <strong>Code:</strong> {code_no}
                    </Col>
                    <Col md={3}>
                        <strong>PO:</strong> {po_no}
                    </Col>
                    <Col md={3}>
                        <strong>Batch:</strong> {batchDisplay}
                    </Col>
                    <Col md={3}>
                        <strong>Expiry:</strong> {modalRowData.expire_date || 'N/A'}
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={4}>
                        <strong>Current Available:</strong>
                        <div className="fs-4 fw-bold text-primary">{available_qty?.toLocaleString()}</div>
                    </Col>
                    <Col md={4}>
                        <strong>Expected Qty:</strong>
                        <div className="fs-4 fw-bold">{expected_qty?.toLocaleString()}</div>
                    </Col>
                    <Col md={4}>
                        <strong>Difference:</strong>
                        <div className={`fs-4 fw-bold text-${type === 'gain' ? 'success' : type === 'loss' ? 'danger' : 'info'}`}>
                            {diff > 0 ? '+' : ''}{diff?.toLocaleString()}
                        </div>
                    </Col>
                </Row>

                {type !== 'equal' && (
                    <Form.Group className="mb-3">
                        <Form.Label>Adjusted Qty (system calculated)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={adjustedQty}
                            className="fw-bold fs-5 text-center"
                            readOnly
                        />
                        <Form.Text>
                            System suggests: <strong>{type === 'gain' ? gain_qty : loss_qty}</strong> units.
                            This is calculated automatically based on expected qty.
                        </Form.Text>
                    </Form.Group>
                )}

                {type === 'gain' && (
                    <Accordion defaultActiveKey="gain-fields">
                        <Accordion.Item eventKey="gain-fields">
                            <Accordion.Header>
                                <strong>Gain Metadata</strong>
                                <Badge bg="success" className="ms-2">Editable</Badge>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Supplier</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={extraFields.supplier_id}
                                                onChange={(e) => updateField('supplier_id', parseInt(e.target.value, 10) || '')}
                                            >
                                                <option value="">Select Supplier</option>
                                                {suppliers.map(s => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Manufacturer</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={extraFields.manufacturer_id}
                                                onChange={(e) => updateField('manufacturer_id', parseInt(e.target.value, 10) || '')}
                                            >
                                                <option value="">Select Manufacturer</option>
                                                {manufacturers.map(m => (
                                                    <option key={m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Expiry Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={extraFields.expire_date || ''}
                                                onChange={(e) => updateField('expire_date', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Unit Price (USD)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.001"
                                                min="0"
                                                size="sm"
                                                value={extraFields.unit_price}
                                                onChange={(e) => updateField('unit_price', parseFloat(e.target.value) || 0)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Currency Type</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={extraFields.currency_type}
                                                onChange={(e) => updateField('currency_type', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Currency Rate</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.0001"
                                                min="0"
                                                size="sm"
                                                value={extraFields.currency_rate}
                                                onChange={(e) => updateField('currency_rate', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Currency Amount</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.001"
                                                min="0"
                                                size="sm"
                                                value={extraFields.currency_amount}
                                                onChange={(e) => updateField('currency_amount', parseFloat(e.target.value) || 0)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Comment</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={extraFields.comment || ''}
                                                onChange={(e) => updateField('comment', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Accordion defaultActiveKey="advanced-gain" className="mt-3">
                                    <Accordion.Item eventKey="advanced-gain">
                                        <Accordion.Header>Advanced Fields</Accordion.Header>
                                        <Accordion.Body>
                                            <Row className="g-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Pack No</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            value={extraFields.pack_no || ''}
                                                            onChange={(e) => updateField('pack_no', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Pack Size</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            value={extraFields.pack_size || ''}
                                                            onChange={(e) => updateField('pack_size', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Mfg Date</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            size="sm"
                                                            value={extraFields.mfg_date || ''}
                                                            onChange={(e) => updateField('mfg_date', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {type === 'loss' && (
                    <Accordion defaultActiveKey="loss-fields">
                        <Accordion.Item eventKey="loss-fields">
                            <Accordion.Header>
                                <strong>Loss Metadata</strong>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Center (Warehouse)</Form.Label>
                                            <Form.Control type="text" size="sm" readOnly plaintext
                                                value={`ID: ${extraFields.center_id}`}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                <hr />

                <h6>Proposed Changes:</h6>
                {changes && changes.length > 0 ? (
                    <Table striped hover bordered size="sm">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '80px' }}>Action</th>
                                <th style={{ width: '150px' }}>Table</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {changes.map((c) => (
                                <tr key={`change-${c.table}-${c.action}-${c.description.substring(0, 20)}`}>
                                    <td>
                                        <Badge bg={c.action === 'INSERT' ? 'success' : 'warning'}>
                                            {c.action}
                                        </Badge>
                                    </td>
                                    <td>{c.table}</td>
                                    <td>{c.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <p className="text-muted text-center py-3">No changes needed.</p>
                )}

                {reduction_details && reduction_details.length > 0 && (
                    <>
                        <h6 className="mt-3">Loss Distribution (Approach C - Largest First):</h6>
                        <Table striped hover bordered size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Detail ID</th>
                                    <th>From Qty</th>
                                    <th>To Qty</th>
                                    <th>Loss Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reduction_details.map((r) => (
                                    <tr key={`reduction-${r.detail_id}-${r.from_qty}-${r.to_qty}`}>
                                        <td>{r.detail_id}</td>
                                        <td className="text-end">{r.from_qty?.toLocaleString()}</td>
                                        <td className="text-end">{r.to_qty?.toLocaleString()}</td>
                                        <td className="text-end text-danger fw-bold">{r.loss_qty?.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} disabled={executing || executingLocal}>
                    Cancel
                </Button>
                {type !== 'equal' && (
                    <Button
                        variant="danger"
                        onClick={handleExecuteClick}
                        disabled={executing || executingLocal}
                    >
                        {executing || executingLocal ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" /> Executing...
                            </>
                        ) : (
                            'Execute Reconciliation'
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default ReconcileModal;
